import { getRequest as get } from 'api/utils/HttpMethods';
import queryString from 'api/utils/queryStringBuilder';

import {
  createEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from 'api/utils/queries';

import { UseQueryResult } from '@tanstack/react-query';
import { Credit, Debit, isStatement, Statement } from 'interfaces';
import { generateGetSignedUrl } from 'api/action';

interface StatementData {
  statementCredits: Credit[];
  statementDebits: Debit[];
  statementTotalAmount: number;
  overhead: number;
}

export const statementQueryKeys = {
  all: ['statements'],
  list: (siteID?: string) => [...statementQueryKeys.all, siteID],
  listByUser: (siteID: string | string[], userID: string) => [
    ...statementQueryKeys.all,
    siteID,
    userID,
  ],
  getStatementTotalByProviderInRange: (
    providerID: string,
    siteID: string,
    stateDate: string,
    endDate: string
  ) => [...statementQueryKeys.all, providerID, siteID, stateDate, endDate],
  getStatementData: (s3Path: string) => [
    ...statementQueryKeys.all,
    'statementData',
    s3Path,
  ],
};

/**
 * @description Fetches the list of statements, optionally scoped to a site.
 * @param {Object} params - Parameters for the request.
 * @param {string} params.siteID - The site to scope the statements to.
 * @returns {Promise<Statement[]>} A promise that resolves to an array of Statement objects.
 */
const listStatements = async ({
  siteID,
}: {
  siteID?: string;
}): Promise<Statement[]> =>
  await get<Statement[]>(
    'QBilling',
    `/resource/statements/all${queryString({ siteID })}`
  );

/**
 * @description Fetches the list of statements for a specific provider and site from the server.
 * @param {Object} params - Parameters for the request.
 * @param {string} params.userID - The ID of the user.
 * @param {string} params.siteID - The ID of the site.
 * @returns {Promise<Statement[]>} A promise that resolves to an array of Statement objects.
 */
const listStatementsByUser = async ({
  userID,
  siteID,
}: {
  userID: string;
  siteID: string | string[];
}): Promise<Statement[]> =>
  await get<Statement[]>(
    'QBilling',
    `/resource/statements/user${queryString({ userID, siteID })}`
  );

/**
 * @description Fetches statement data from an S3 path.
 * @param {Object} params - Parameters for the request.
 * @param {string} params.s3Path - The S3 path to the statement data.
 * @returns {Promise<StatementData>} A promise that resolves to the statement data.
 */
const getStatementData = async ({
  s3Path,
}: {
  s3Path: string;
}): Promise<StatementData> => {
  if (!s3Path) throw new Error('No s3Path provided');
  const signedUrl = await generateGetSignedUrl({
    bucket: `statement-data-files-${import.meta.env.VITE_ACCOUNT_ID}`,
    key: s3Path,
    type: 'GET',
  });
  const response = await fetch(signedUrl);
  if (!response.ok) {
    throw new Error('Failed to fetch statement data');
  }
  const res = await response.json();
  return res;
};

/**
 * @description Hook to fetch statement data using React Query.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.s3Path - The S3 path to the statement data.
 * @returns {UseQueryResult<StatementData, Error>} The result of the query.
 */
export const useGetStatementData = ({
  s3Path,
}: {
  s3Path: string;
}): UseQueryResult<StatementData, Error> =>
  useGetQuery<StatementData>({
    queryKey: statementQueryKeys.getStatementData(s3Path),
    fetchFunction: getStatementData,
    isEntity: (data): data is StatementData => typeof data === 'object',
    functionName: 'getStatementData',
    params: { s3Path },
    enabled: !!s3Path,
  });

/**
 * @description Creates a new statement on the server.
 * @param {Statement} statement - The statement to create.
 * @returns {Promise<Statement>} A promise that resolves to the created Statement object.
 */
const createStatement = async (statement: Statement): Promise<Statement> => {
  return createEntity<Statement>({
    entity: statement,
    functionName: 'createStatement',
    isEntity: isStatement,
    path: '/resource/statements',
  });
};

/**
 * @description Hook to create a new statement using React Query.
 * @returns {UseMutationResult<Statement, Error>} The result of the mutation.
 */
export const useCreateStatement = () => {
  return useMutationQuery<Statement>({
    errorMessage: 'Statement Creation Failed',
    mutationFn: createStatement,
    successMessage: 'Statement Created',
    queryKey: statementQueryKeys.all,
  });
};

/**
 * @description Hook to fetch the list of statements using React Query.
 * @param {Object} params - Parameters for the hook.
 * @param {boolean} params.isAdmin - Whether the user is an admin.
 * @returns {UseQueryResult<Statement[], Error>} The result of the query.
 */
export const useListStatements = ({
  isAdmin,
  siteID,
}: {
  isAdmin: boolean;
  siteID?: string;
}): UseQueryResult<Statement[], Error> =>
  useListQuery<Statement>({
    queryKey: statementQueryKeys.list(siteID),
    fetchFunction: listStatements,
    isEntity: isStatement,
    functionName: 'listStatements',
    params: { siteID },
    enabled: isAdmin && !!siteID,
  });

/**
 * @description Hook to fetch the list of statements for all providers associated with a user and site.
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.userID - The ID of the userID.
 * @param {string} params.siteID - The ID of the site.
 * @returns {UseQueryResult<Statement[], Error>} The result of the query.
 */
export const useListStatementsByUser = ({
  userID,
  siteID,
}: {
  userID: string;
  siteID: string | string[];
}): UseQueryResult<Statement[], Error> =>
  useListQuery<Statement>({
    queryKey: statementQueryKeys.listByUser(siteID, userID),
    fetchFunction: listStatementsByUser,
    isEntity: isStatement,
    functionName: 'listStatementsByUser',
    params: { userID, siteID },
    enabled: !!userID && (Array.isArray(siteID) ? siteID.length > 0 : !!siteID),
  });
